import { useState, useCallback, useEffect } from 'react';
import { useMedplum } from '@medplum/react';
import { ConversationThread, ConversationMessage } from '../models/conversation';
import { Communication, Binary } from '@medplum/fhirtypes';

/**
 * Props for the useConversation hook
 */
interface UseConversationProps {
  // ID of the thread conversation to fetch
  threadId?: string;
  // Flag to skip TTS generation (for Basic and Free tier users)
  skipTTS?: boolean;
}

/**
 * Hook for managing a conversation thread with messages
 * Uses the FHIR partOf relationship pattern for better reliability
 * Adapted for the client portal where the current user is always the client
 */
export function useConversation({ threadId, skipTTS = false }: UseConversationProps) {
  const medplum = useMedplum();
  const [thread, setThread] = useState<ConversationThread | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isBotProcessing, setIsBotProcessing] = useState<boolean>(false);
  
  // Get the current client ID from the profile (still Patient resource internally)
  const patientId = medplum.getProfile()?.id;

  /**
   * Load an existing conversation thread and its messages
   */
  const loadThread = useCallback(async (id: string) => {
    try {
      const timestamp = new Date().toISOString();
      console.log(`[${timestamp}] Loading thread with ID:`, id);
      setIsLoading(true);
      setError(null);

      // First get the thread document with cache busting
      const threadResource = await medplum.readResource('Communication', id, {
        cache: 'no-cache' // Force fresh data, bypass cache
      });
      
      console.log('Thread resource found:', {
        id: threadResource.id,
        subject: threadResource.subject?.reference,
        status: threadResource.status,
        lastUpdated: threadResource.meta?.lastUpdated
      });
      
      // Verify that this thread belongs to the current client
      const threadClientRef = threadResource.subject?.reference;
      const expectedClientRef = `Patient/${patientId}`; // Still using Patient resource internally
      
      if (threadClientRef !== expectedClientRef) {
        console.error('Thread does not belong to current client');
        setError('You do not have permission to access this conversation');
        setIsLoading(false);
        return;
      }
      
      // Get all messages for this thread
      const MAX_MESSAGES = 100; // Set a higher limit to get all messages
      
      // Create search params with explicit count to overcome the default limit
      const searchParams = {
        'part-of': `Communication/${id}`,
        _sort: 'sent', // Always sort oldest first for consistent display
        _count: MAX_MESSAGES // Request more than the default 20
      };
      
      console.log(`Fetching messages with max count ${MAX_MESSAGES} and cache busting...`);
      const directMessageResult = await medplum.searchResources('Communication', searchParams, {
        cache: 'no-cache' // Force fresh data, bypass cache
      });
      
      // Log each message for debugging
      console.log('Messages retrieved:', directMessageResult.map(msg => ({
        id: msg.id,
        sent: msg.sent,
        contentPreview: msg.payload?.[0]?.contentString?.substring(0, 30) + (msg.payload?.[0]?.contentString?.length ?? 0 > 30 ? '...' : ''),
        isTranscribingPlaceholder: msg.payload?.[0]?.contentString === '[Audio message - Transcribing...]',
        isUser: msg.sender?.reference?.includes('Patient/') 
      })));
      
      // Create the thread with directly queried messages
      const loadedThread = ConversationThread.fromCommunication({
        comm: threadResource,
        threadMessageComms: directMessageResult
      });
      
      const endTimestamp = new Date().toISOString();
      console.log(`[${endTimestamp}] Setting thread with ${directMessageResult.length} messages`);
      setThread(loadedThread);
    } catch (err) {
      console.error('Error loading conversation:', err);
      setError(`Error loading conversation: ${(err as Error).message}`);
    } finally {
      setIsLoading(false);
    }
  }, [medplum, patientId]);

  /**
   * Send a text message in the conversation
   * @param text The text message to send
   * @param skipBotProcessing If true, just store the message without calling the bot (useful for placeholders)
   */
  const sendTextMessage = useCallback(async (text: string, skipBotProcessing = false) => {
    // Make sure the user input has actual content, not just whitespace
    const trimmedText = text.trim();
    
    if (!trimmedText || !threadId) {
      setError('Cannot send empty message or missing thread ID');
      return null;
    }

    try {
      // Create the message resource
      const messageComm = await medplum.createResource<Communication>({
        resourceType: 'Communication',
        status: 'completed',
        sent: new Date().toISOString(),
        subject: { reference: `Patient/${patientId}` },
        sender: { reference: `Patient/${patientId}` },
        payload: [{ contentString: trimmedText }],
        partOf: [{ reference: `Communication/${threadId}` }]
      });

      // Update the last activity date on the thread
      await medplum.patchResource('Communication', threadId, [
        {
          op: 'add',
          path: '/extension/0/valueDateTime',
          value: new Date().toISOString()
        }
      ]);

      // Add the new message to the thread
      const newMessage = ConversationMessage.fromCommunication({ comm: messageComm });
      
      // Update the thread with the new message
      setThread(currentThread => {
        if (!currentThread) return null;
        
        const updatedMessages = [...currentThread.messages, newMessage].sort(
          (a, b) => a.messageOrder - b.messageOrder
        );
        
        return new ConversationThread({
          messages: updatedMessages,
          originalCommunication: currentThread.originalCommunication
        });
      });

      // Only process with reflection-guide bot if not skipping bot processing
      if (!skipBotProcessing) {
        try {
          console.log('Executing reflection-guide bot with text input and threadId:', threadId);
          // Set bot processing state to true
          setIsBotProcessing(true);
          console.log('Bot processing started for text message');
          
          // Generate client fingerprint for tracking
          const clientFingerprint = `client-${navigator?.userAgent?.substring(0, 50) || 'unknown'}-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
          
          const response = await medplum.executeBot(
            {
              system: 'https://progressnotes.app',
              value: 'reflection-guide'
            },
            {
              patientId,
              threadId,
              textInput: trimmedText,
              skipMessageCreation: true, // Don't create a new message, update the existing one
              returnTranscriptionOnly: false, // Explicitly indicate we want a response, not just transcription
              skipTTS: skipTTS, // Skip TTS for Basic/Free tier users
              // Add client tracking for debugging cross-device issues
              clientDevice: navigator?.userAgent || 'unknown',
              clientFingerprint,
              clientTimestamp: new Date().toISOString()
            }
          );
        
        console.log('Received response from reflection-guide bot:', {
          success: response.success,
          hasResponseContent: !!response.responseContent,
          threadId: response.threadId,
          hasAudio: !!response.audioData
        });
        
        if (!response.success) {
          console.error('Bot execution failed:', response.error);
          setError(response.error || 'Failed to process your message');
        }
        
        // Refresh the thread to get the bot's response
        await loadThread(threadId);
        
        return messageComm.id;
      } catch (botError) {
        console.error('Error processing message with bot:', botError);
        setError(`Error processing message: ${(botError as Error).message}`);
        return messageComm.id;
      } finally {
        // Set bot processing state back to false
        setIsBotProcessing(false);
        console.log('Bot processing completed for text message');
      }
      } else {
        // If skipping bot processing, just return the message ID
        return messageComm.id;
      }
    } catch (err) {
      setError(`Failed to send message: ${(err as Error).message}`);
      return null;
    }
  }, [medplum, threadId, patientId, loadThread]);

  /**
   * Helper function to convert a Blob to base64
   */
  const convertBlobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64data = reader.result?.toString().split(',')[1];
        if (base64data) {
          resolve(base64data);
        } else {
          reject(new Error('Failed to convert audio to base64'));
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  /**
   * Send an audio recording for transcription and processing 
   * DIRECT IMPLEMENTATION OF PROGRESS2-BASE - NO EXTRA STEPS
   */
  const sendAudioRecording = useCallback(async (audioBlob: Blob) => {
    if (!threadId || !patientId) {
      setError('Thread ID and patient ID are required');
      return null;
    }

    // Set bot processing state to true
    setIsBotProcessing(true);
    console.log('Bot processing started for audio transcription');

    try {
      // CRITICAL: We need to make this function EXACTLY match progress2-base
      console.log('Audio recording stopped - processing with EXACT progress2-base implementation');
      
      // Create a placeholder message for the user's audio input without bot processing
      // This prevents Claude from responding to the placeholder text
      console.log('Creating audio transcription placeholder with skipBotProcessing=true...');
      const placeholderMessageId = await sendTextMessage("[Audio message - Transcribing...]", true);
      console.log('Placeholder created with ID:', placeholderMessageId);
      
      // Convert blob to base64 - EXACT MATCH to progress2-base
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onloadend = () => {
          const base64data = reader.result?.toString().split(',')[1];
          if (base64data) {
            resolve(base64data);
          } else {
            reject(new Error('Failed to convert audio to base64'));
          }
        };
        reader.onerror = reject;
        reader.readAsDataURL(audioBlob);
      });
      
      const base64data = await base64Promise;
      console.log('Audio converted to base64, length:', base64data.length);
      
      // Create Binary resource - EXACT MATCH to progress2-base 
      const binary = await medplum.createResource({
        resourceType: 'Binary',
        contentType: audioBlob.type, 
        data: base64data
      }) as Binary & { id: string };
      console.log('Binary resource created with ID:', binary.id);
      
      // Refresh thread to show placeholder
      console.log('Refreshing thread to show placeholder...');
      await loadThread(threadId);
      
      // STEP 1: First call with returnTranscriptionOnly to just get transcription without response
      // This fixes the bug where Claude responds to the placeholder text
      console.log(`STEP 1: Executing reflection-guide bot for transcription only with audioBinaryId: ${binary.id}, placeholderMessageId: ${placeholderMessageId}`);
      // Generate client fingerprint for tracking
      const clientFingerprint = `audio-${navigator?.userAgent?.substring(0, 50) || 'unknown'}-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
      
      const transcriptionResponse = await medplum.executeBot(
        {
          system: 'https://progressnotes.app',
          value: 'reflection-guide'
        }, 
        {
          patientId,
          threadId,
          audioBinaryId: binary.id,
          placeholderMessageId,
          returnTranscriptionOnly: true,  // Critical fix - only update placeholder without generating response
          // Add client tracking for debugging cross-device issues
          clientDevice: navigator?.userAgent || 'unknown',
          clientFingerprint,
          clientTimestamp: new Date().toISOString()
        }
      );
      
      if (!transcriptionResponse.success) {
        console.error('Transcription failed:', transcriptionResponse.error);
        setError(`Failed to transcribe audio: ${transcriptionResponse.error}`);
        return null;
      }
      
      // Get the transcribed text and trim any extra whitespace
      const transcribedText = transcriptionResponse.transcription?.trim();
      console.log('Transcription successful:', transcribedText);
      
      // Short wait to ensure placeholder update is propagated
      console.log('Waiting for transcription update to propagate...');
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Refresh to show the updated placeholder with transcription
      console.log('Refreshing thread to show transcription...');
      await loadThread(threadId);
      
      // STEP 2: Second call to generate AI response to the transcribed text
      // Use skipMessageCreation flag to avoid duplicate message creation
      console.log(`STEP 2: Executing reflection-guide bot for AI response using transcribed text with skipMessageCreation=true`);
      // Generate a new fingerprint for the response step
      const responseFingerprint = `response-${navigator?.userAgent?.substring(0, 50) || 'unknown'}-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
      
      const responseResult = await medplum.executeBot(
        {
          system: 'https://progressnotes.app',
          value: 'reflection-guide'
        }, 
        {
          patientId,
          threadId,
          textInput: transcribedText,
          returnTranscriptionOnly: false, // We want AI response now
          skipMessageCreation: true, // Skip creating another message since we already updated the placeholder
          skipTTS: skipTTS, // Skip TTS for Basic/Free tier users
          // Add client tracking for debugging cross-device issues
          clientDevice: navigator?.userAgent || 'unknown',
          clientFingerprint: responseFingerprint,
          clientTimestamp: new Date().toISOString()
        }
      );
      
      if (!responseResult.success) {
        console.error('AI response generation failed:', responseResult.error);
        setError(`Failed to generate AI response: ${responseResult.error}`);
        return null;
      }
      
      // Wait for server updates to propagate
      console.log('Waiting for AI response to propagate...');
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Final refresh to get all updated messages
      console.log('Final refresh to get all updated messages...');
      await loadThread(threadId);
      
      return responseResult.communicationId;
    } catch (error) {
      console.error('Error sending audio:', error);
      setError(`Error sending audio: ${(error as Error).message}`);
      return null;
    } finally {
      // Set bot processing state back to false
      setIsBotProcessing(false);
      console.log('Bot processing completed for audio transcription');
    }
  }, [threadId, patientId, sendTextMessage, medplum, loadThread]);

  // Load the thread when threadId changes
  useEffect(() => {
    if (threadId) {
      loadThread(threadId);
    } else {
      setThread(null);
    }
  }, [threadId, loadThread]);

  /**
   * Regenerate audio for a specific message
   */
  const regenerateAudio = useCallback(async (messageId: string) => {
    if (!threadId || !patientId) {
      setError('Thread ID and patient ID are required');
      return false;
    }

    try {
      console.log('Regenerating audio for message:', messageId);
      
      // Execute the reflection-guide bot with the regenerateTTS flag
      const response = await medplum.executeBot(
        {
          system: 'https://progressnotes.app',
          value: 'reflection-guide'
        },
        {
          patientId,
          threadId,
          messageId,
          regenerateTTS: true
        }
      );
      
      console.log('Regenerate audio response:', {
        success: response.success,
        messageId: response.messageId
      });
      
      if (!response.success) {
        console.error('Audio regeneration failed:', response.error);
        setError(`Failed to regenerate audio: ${response.error}`);
        return false;
      }
      
      // Refresh the thread to show the updated audio
      await loadThread(threadId);
      
      return true;
    } catch (error) {
      console.error('Error regenerating audio:', error);
      setError(`Error regenerating audio: ${(error as Error).message}`);
      return false;
    }
  }, [medplum, threadId, patientId, loadThread]);

  return {
    thread,
    isLoading,
    error,
    isBotProcessing,
    loadThread,
    sendTextMessage,
    sendAudioRecording,
    regenerateAudio
  };
}